/**
 * PricingPage — the cross-lab PRICING + BILLING console (MANAGEMENT 14). Three jobs under one screen, split
 * across two tabs:
 *   • CATALOGUE — a professional pricing-table of the PlanCatalogue tiers with full CRUD (publish / reissue /
 *     retire / restore) + per-plan operator actions (assign a plan to a lab · create a checkout).
 *   • BILLING — a per-lab payment history (SubscriptionPayment list) + a "create checkout" action showing the
 *     Chargily test/simulated flow.
 *
 * AUTHORITY — the super-admin platform plane (`est_admin_plateforme`, re-checked LIVE server-side on the
 * `platformRoute` chain, capability `management.pricing.manage`). The route already rides <PlatformAdminGuard>
 * (index.tsx); this screen ALSO renders its own <ForbiddenState> defensively and short-circuits offline + the
 * cloud-only "module not mounted on this install" (404/501) state. Full 5-state DoD: forbidden / offline /
 * cloud-absent / loading / error / empty + the live surface. No PHI on this seam (the commercial layer, L-4).
 *
 * All server data lives in TanStack Query (pricing.api.ts); every mutation invalidates the affected platform
 * key. i18n registered additively at module-eval (idempotent latch).
 */
import { useMemo, useState } from "react";
import { Button, Card, ErrorState, ForbiddenState, LoadingState, OfflineState, Tabs } from "@/ui";
import { useT, useUiStore } from "@/app";
import { ALL_MANAGEMENT_CAPS } from "../caps";
import { useIsPlatformAdmin } from "../session";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";
import { PricingTable } from "../components/PricingTable";
import { PlanFormModal } from "../components/PlanFormModal";
import { AssignPlanModal } from "../components/AssignPlanModal";
import { CheckoutModal } from "../components/CheckoutModal";
import { RetirePlanModal } from "../components/RetirePlanModal";
import { PaymentsPanel } from "../components/PaymentsPanel";
import { isManagementAbsent, usePlans, useRestorePlan } from "./pricing.api";
import { registerPricingMessages } from "./pricing.messages";
import type { PlanRow } from "./pricing.types";

// Register the screen's i18n additively at module-eval (idempotent latch). The shell owns nav.section.platform
// + the sibling nav.mgmt.* labels; this adds nav.mgmt.pricing + every mgmt.pricing.* key.
registerPricingMessages();

type TabId = "catalogue" | "billing";

export function PricingPage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const isAdmin = useIsPlatformAdmin();

  const [tab, setTab] = useState<TabId>("catalogue");
  const [showRetired, setShowRetired] = useState(true);

  // Modal cursors — separate so the four actions are independent. `undefined` = closed. For the plan-form,
  // `null` = the CREATE flow (vs a row = EDIT).
  const [editing, setEditing] = useState<PlanRow | null | undefined>(undefined);
  const [assigning, setAssigning] = useState<PlanRow | undefined>(undefined);
  const [checkingOut, setCheckingOut] = useState<PlanRow | undefined>(undefined);
  const [checkoutLabId, setCheckoutLabId] = useState<string | undefined>(undefined);
  const [retiring, setRetiring] = useState<PlanRow | undefined>(undefined);

  const enabled = isAdmin && online;
  const query = usePlans(enabled, { actifSeulement: !showRetired });
  const restore = useRestorePlan();

  const plans = useMemo(() => query.data?.items ?? [], [query.data]);

  const header = (
    <PageHeader
      title={t("mgmt.pricing.title")}
      subtitle={t("mgmt.pricing.subtitle")}
      actions={
        isAdmin && online && !isManagementAbsent(query.error) && !query.isError ? (
          <Button onClick={() => setEditing(null)}>{t("mgmt.pricing.action.newPlan")}</Button>
        ) : undefined
      }
    />
  );

  if (!isAdmin) {
    return (
      <ForbiddenState
        requiredCapabilities={ALL_MANAGEMENT_CAPS}
        message="Cette console est réservée aux administrateurs de la plateforme Pharmakon."
      />
    );
  }

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={plans.length > 0} />
      </Screen>
    );
  }

  // Cloud-only module on a local box → its routes 404/501. Surface the clean "not available here" notice.
  if (isManagementAbsent(query.error)) {
    return (
      <Screen>
        {header}
        <Card title={t("mgmt.unavailable.title")}>
          <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{t("mgmt.unavailable.desc")}</p>
        </Card>
      </Screen>
    );
  }

  const catalogueContent = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--nv-s-3)", flexWrap: "wrap", alignItems: "center" }}>
        <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{t("mgmt.pricing.catalogue.subtitle")}</p>
        <label style={{ display: "inline-flex", alignItems: "center", gap: "var(--nv-s-2)", fontSize: "var(--nv-fs-body)" }}>
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          {t("mgmt.pricing.showRetired")}
        </label>
      </div>

      {query.isLoading ? (
        <LoadingState variant="table" rows={4} />
      ) : query.isError ? (
        <ErrorState message={query.error.message} code={query.error.code} onRetry={() => void query.refetch()} />
      ) : plans.length === 0 ? (
        <Card>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)", alignItems: "flex-start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
              <strong style={{ fontSize: "var(--nv-fs-h3)" }}>{t("mgmt.pricing.empty.title")}</strong>
              <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{t("mgmt.pricing.empty.desc")}</p>
            </div>
            <Button onClick={() => setEditing(null)}>{t("mgmt.pricing.empty.action")}</Button>
          </div>
        </Card>
      ) : (
        <PricingTable
          plans={plans}
          onAssign={setAssigning}
          onCheckout={(plan) => {
            setCheckoutLabId(undefined);
            setCheckingOut(plan);
          }}
          onEdit={setEditing}
          onRetire={setRetiring}
          onRestore={(plan) => restore.mutate({ code: plan.code })}
        />
      )}
    </div>
  );

  const billingContent = (
    <PaymentsPanel
      enabled={enabled}
      onCreateCheckout={(labId) => {
        // Billing-tab checkout needs a plan to price against — default to the first active plan; the modal
        // still lets the operator confirm the plan-driven amount + cycle. If no active plan, no-op silently.
        const activePlan = plans.find((p) => p.actif) ?? plans[0];
        if (!activePlan) return;
        setCheckoutLabId(labId);
        setCheckingOut(activePlan);
      }}
    />
  );

  return (
    <Screen>
      {header}
      <Tabs
        aria-label={t("mgmt.pricing.title")}
        value={tab}
        onChange={(id) => setTab(id as TabId)}
        items={[
          { id: "catalogue", label: t("mgmt.pricing.tab.catalogue"), content: catalogueContent },
          { id: "billing", label: t("mgmt.pricing.tab.billing"), content: billingContent },
        ]}
      />

      <PlanFormModal plan={editing} onClose={() => setEditing(undefined)} />
      <AssignPlanModal plan={assigning} onClose={() => setAssigning(undefined)} />
      <CheckoutModal
        plan={checkingOut}
        presetLabId={checkoutLabId}
        onClose={() => {
          setCheckingOut(undefined);
          setCheckoutLabId(undefined);
        }}
      />
      <RetirePlanModal plan={retiring} onClose={() => setRetiring(undefined)} />
    </Screen>
  );
}
